import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  getToken, setToken, removeToken, getUserData, setUserData,
  isTokenValid, getUserFromToken 
} from '../utils/helpers';
import { authAPI } from '../api/endpoints';
import toast from 'react-hot-toast';
import jwtDecode from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    setLoading(true);
    const token = getToken();
    const userData = getUserData();

    if (token && isTokenValid() && userData) {
      setUser(userData);
      setIsAuthenticated(true);
    } else if (token && isTokenValid()) {
      try {
        // Get user ID from token
        const decoded = jwtDecode(token);
        const userId = decoded?.data?.user?.id || decoded?.user?.id || null;
        
        const response = await authAPI.getProfile();
        const userData = response.data;
        // Add ID from token if not in response
        if (userId && !userData.id) {
          userData.id = userId;
        }
        // Fetch user role
        try {
          const roleRes = await authAPI.getUserRole();
          userData.capabilities = roleRes.data.capabilities || {};
          userData.is_admin = roleRes.data.is_admin || false;
          userData.roles = roleRes.data.roles || [];
        } catch (e) {
          console.log('Could not fetch user role');
        }
        setUser(userData);
        setUserData(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        if (error.response?.status === 401) {
          removeToken();
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } else {
      if (token) {
        removeToken();
      }
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user_meta, user_display_name, user_email, user_nicename } = response.data;
      
      setToken(token);
      
      // Decode JWT to get user ID
      let userId = null;
      try {
        const decoded = jwtDecode(token);
        userId = decoded?.data?.user?.id || decoded?.user?.id || null;
        console.log('User ID from token:', userId);
      } catch (decodeError) {
        console.error('Failed to decode token:', decodeError);
      }
      
      const userData = {
        id: userId || user_meta?.id || null,
        display_name: user_display_name,
        email: user_email,
        username: user_nicename,
        ...user_meta,
        is_admin: false,
        capabilities: {},
        roles: [],
      };
      
      // Fetch user role to check if admin
      try {
        const roleRes = await authAPI.getUserRole();
        userData.capabilities = roleRes.data.capabilities || {};
        userData.is_admin = roleRes.data.is_admin || false;
        userData.roles = roleRes.data.roles || [];
      } catch (e) {
        console.log('Could not fetch user role');
      }
      
      setUser(userData);
      setUserData(userData);
      setIsAuthenticated(true);
      
      toast.success('Welcome back! 🎉');
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      const { user_id, username, display_name, referral_code, referral_link } = response.data;
      
      toast.success('Registration successful! 🎉 Please login.');
      return { 
        success: true, 
        user_id, 
        username, 
        display_name, 
        referral_code, 
        referral_link 
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const updateUser = (data) => {
    const updatedUser = { ...user, ...data };
    setUser(updatedUser);
    setUserData(updatedUser);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};